import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronUp, ChevronDown, ArrowUp, ArrowDown, X } from 'lucide-react';

const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const [yearStr, monthStr, dayStr] = String(value).split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const CustomDatePicker = ({ 
  value, 
  onChange, 
  label, 
  darkMode, 
  inputClassName = "ta-date-input", 
  iconClassName = "ta-input-icon" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => parseDateValue(value) || new Date());
  const containerRef = useRef(null);

  // Sync viewDate when value changes from outside
  useEffect(() => {
    if (!value) return;
    const parsedDate = parseDateValue(value);
    if (parsedDate) setViewDate(parsedDate);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const startOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleYearChange = (year) => {
      setViewDate(new Date(year, viewDate.getMonth(), 1));
  };

  const currentYear = viewDate.getFullYear();
  const currentMonth = viewDate.getMonth();
  
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const days = [];
  const totalDays = daysInMonth(currentYear, currentMonth);
  const firstDay = startOfMonth(currentYear, currentMonth);

  // Previous month trailing days
  const prevMonthDays = daysInMonth(currentYear, currentMonth - 1);
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({
      day: prevMonthDays - i,
      month: currentMonth - 1,
      year: currentYear,
      isCurrentMonth: false
    });
  }

  // Current month days
  for (let i = 1; i <= totalDays; i++) {
    days.push({
      day: i,
      month: currentMonth,
      year: currentYear,
      isCurrentMonth: true
    });
  }

  // Next month leading days
  const remainingSlots = 42 - days.length; // 6 rows of 7 days
  for (let i = 1; i <= remainingSlots; i++) {
    days.push({
      day: i,
      month: currentMonth + 1,
      year: currentYear,
      isCurrentMonth: false
    });
  }

  const handleDateClick = (d) => {
    const selectedDate = new Date(d.year, d.month, d.day);
    onChange(toLocalDateString(selectedDate));
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    onChange(toLocalDateString(today));
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setIsOpen(false);
  };

  const isSelected = (d) => {
    if (!value) return false;
    const date = parseDateValue(value);
    if (!date) return false;
    return date.getFullYear() === d.year && date.getMonth() === d.month && date.getDate() === d.day;
  };

  return (
    <div className={`custom-datepicker-container ${darkMode ? 'dark' : ''}`} ref={containerRef}>
      <div className="ta-date-input-wrapper" onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          className={inputClassName}
          value={(() => {
            const parsedValue = parseDateValue(value);
            return parsedValue ? parsedValue.toLocaleDateString('en-GB') : "";
          })()}
          readOnly
          aria-label={label}
          placeholder="DD/MM/YYYY"
        />
        <CalendarIcon className={iconClassName} size={14} />
      </div>

      {isOpen && (
        <div className="ta-calendar-dropdown">
          <div className="ta-calendar-header">
            <div className="ta-calendar-month-year">
              {monthNames[currentMonth]} {currentYear} <ChevronDown size={14} />
            </div>
            <div className="ta-calendar-nav">
              <button type="button" onClick={handlePrevMonth}><ArrowUp size={20} /></button>
              <button type="button" onClick={handleNextMonth}><ArrowDown size={20} /></button>
            </div>
          </div>
          
          <div className="ta-calendar-weekdays">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
              <div key={d} className="ta-weekday">{d}</div>
            ))}
          </div>
          
          <div className="ta-calendar-grid">
            {days.map((d, index) => (
              <div
                key={index}
                className={`ta-day ${d.isCurrentMonth ? '' : 'ta-day-muted'} ${isSelected(d) ? 'ta-day-selected' : ''}`}
                onClick={() => handleDateClick(d)}
              >
                {d.day}
              </div>
            ))}
          </div>
          
          <div className="ta-calendar-footer">
            <button type="button" className="ta-footer-btn clear" onClick={handleClear}>Clear</button>
            <button type="button" className="ta-footer-btn today" onClick={handleToday}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
